import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import usePageMeta from '../usePageMeta';
import { newsDate, newsLink } from '../newsData';
import { mediaUrl } from '../media';
import { useSiteContent, useContact } from '../SiteContent';
import ContactSection from '../components/ContactSection';
import TeamGrid from '../components/TeamGrid';
import T from '../components/EditableText';
import Testimonials from '../components/Testimonials';
import HeroPicture from '../components/HeroPicture';

/** Textschlüssel je Merkmal — die Beschriftungen stehen in `content/home.ts`. */
const FEATURE_KEYS = [1, 2, 3, 4, 5].map((n) => `home.auszeichnung.${n}`);

const TESTIMONIAL_NUMBERS = [1, 2, 3];

const Home: React.FC = () => {
  const { news } = useSiteContent();
  const contact = useContact();

  // Der Titel trägt die beiden Begriffe, unter denen die Startseite tatsächlich
  // gefunden wird („24-Stunden-Betreuung", „Haushaltshilfe") plus den Ort.
  // Der Markenname steht hinten: unter ihm steht die Seite ohnehin auf Platz 1.
  usePageMeta(
    '24-Stunden-Betreuung & Haushaltshilfe in Gifhorn | Antczak',
    'Häusliche Seniorenbetreuung in Gifhorn: 24-Stunden-Betreuung zu Hause und stundenweise Hauswirtschaft. Familienbetrieb, faire Preise, kostenlose Beratung.',
    { standaloneTitle: true, preloadImage: '/website/img/home-hero.webp' },
  );

  return (
    <>
      {/* Kopf: dunkle Fläche mit Foto, links die Auswahl „Wobei dürfen wir helfen?“ */}
      <section className="shell hx-hero-wrap">
        <div className="hx-hero">
          <div className="hx-hero__body">
            <span className="eyebrow eyebrow--on-image">
              <T k="home.hero.eyebrow" />
            </span>
            <h1 className="h-hero hx-hero__title">
              <T k="home.hero.titel" />{' '}
              <span className="accent">
                <T k="home.hero.titel-akzent" />
              </span>
            </h1>
            <p className="hx-hero__lead">
              <T k="home.hero.lead" />
            </p>

            <div className="hx-pick">
              <div className="hx-pick__title">
                <T k="home.wahl.titel" />
              </div>
              <Link to="/hauswirtschaft" className="hx-pick__row">
                <span className="hx-pick__icon"><Icon name="house" size={18} /></span>
                <span className="hx-pick__text">
                  <strong><T k="home.wahl.hw.titel" /></strong>
                  <span><T k="home.wahl.hw.text" /></span>
                </span>
                <Icon name="chevron-right" size={18} />
              </Link>
              <Link to="/24h-betreuung" className="hx-pick__row">
                <span className="hx-pick__icon"><Icon name="heart-handshake" size={18} /></span>
                <span className="hx-pick__text">
                  <strong><T k="home.wahl.24h.titel" /></strong>
                  <span><T k="home.wahl.24h.text" /></span>
                </span>
                <Icon name="chevron-right" size={18} />
              </Link>
              <Link to="/kontakt" className="btn btn--primary hx-pick__cta">
                <T k="home.hero.cta-beratung" />
              </Link>
              <div className="hx-pick__call">
                <T k="home.wahl.anrufen" />{' '}
                <a href={contact.phoneHref}>{contact.phoneLabel}</a>
              </div>
            </div>
          </div>
          <div className="hx-hero__media">
            <HeroPicture
              src="/website/img/home-hero.jpg"
              alt="Betreuerin umarmt eine Seniorin zu Hause"
              width={900}
              height={1000}
            />
          </div>
        </div>

        {/* Vertrauenszeile direkt unter dem Kopf */}
        <dl className="hx-trust">
          <div>
            <Icon name="calendar-check" size={20} />
            <dt><T k="home.hero.fakt-1.wert" /></dt>
            <dd><T k="home.hero.fakt-1.text" /></dd>
          </div>
          <div>
            <Icon name="phone-call" size={20} />
            <dt><T k="home.hero.fakt-2.wert" /></dt>
            <dd><T k="home.hero.fakt-2.text" /></dd>
          </div>
          <div>
            <Icon name="shield-check" size={20} />
            <dt><T k="home.vertrauen.kasse.wert" /></dt>
            <dd><T k="home.vertrauen.kasse.text" /></dd>
          </div>
          <div>
            <Icon name="users" size={20} />
            <dt><T k="home.vertrauen.familie.wert" /></dt>
            <dd><T k="home.vertrauen.familie.text" /></dd>
          </div>
        </dl>
      </section>

      {/* Ablauf in vier Schritten */}
      <section className="shell section hx-steps">
        <div className="hx-steps__head">
          <span className="eyebrow"><T k="home.ablauf.eyebrow" /></span>
          <h2 className="h-section"><T k="home.ablauf.titel" /></h2>
        </div>
        <ol className="hx-steps__list">
          {[1, 2, 3, 4].map((n) => (
            <li key={n}>
              <span className="hx-steps__num">{n}</span>
              <h3 className="h-card"><T k={`home.ablauf.${n}.titel`} /></h3>
              <p><T k={`home.ablauf.${n}.text`} /></p>
            </li>
          ))}
        </ol>
      </section>

      {/* Leistungen: zwei große Bildkarten */}
      <section className="section--tinted">
        <div className="shell section">
          <h2 className="h-section hx-services__title"><T k="home.leistungen.titel" /></h2>
          <div className="hx-services">
            <Link to="/hauswirtschaft" className="hx-service">
              <img src="/website/img/home-hauswirtschaft.jpg" alt="Haushaltshilfe beim Staubsaugen" width={900} height={520} style={{ objectPosition: 'center top' }} loading="lazy" />
              <div className="hx-service__body">
                <h3 className="h-card"><T k="home.leistungen.hauswirtschaft.titel" /></h3>
                <p><T k="home.leistungen.hauswirtschaft.text" /></p>
                <span className="textlink"><T k="home.leistungen.hauswirtschaft.link" /> <Icon name="arrow-right" size={16} /></span>
              </div>
            </Link>
            <Link to="/24h-betreuung" className="hx-service">
              <img src="/website/img/home-24h.jpg" alt="Betreuerin im Gespräch mit einer Seniorin im Rollstuhl" width={900} height={520} loading="lazy" />
              <div className="hx-service__body">
                <h3 className="h-card"><T k="home.leistungen.24h.titel" /></h3>
                <p><T k="home.leistungen.24h.text" /></p>
                <span className="textlink"><T k="home.leistungen.24h.link" /> <Icon name="arrow-right" size={16} /></span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Inhaber mit dem, was den Betrieb ausmacht */}
      <section className="shell section hx-owner">
        <div className="hx-owner__photo">
          <img src="/website/img/roman-portrait.jpg" alt="Roman Antczak, Inhaber der Seniorenbetreuung Antczak" width={720} height={900} loading="lazy" />
        </div>
        <div className="hx-owner__body">
          <span className="eyebrow"><T k="home.inhaber.claim" /></span>
          <h2 className="h-section"><T k="home.inhaber.titel" /></h2>
          <div className="prose">
            <p><T k="home.inhaber.absatz-1" /></p>
            <p><T k="home.inhaber.absatz-2" /></p>
          </div>
          <ul className="hx-owner__values">
            {FEATURE_KEYS.map((key) => (
              <li key={key}><Icon name="check" size={16} /> <T k={key} /></li>
            ))}
          </ul>
          <div className="hx-owner__sign">
            <img src="/website/img/unterschrift.png" alt="Unterschrift Roman Antczak" loading="lazy" />
            <div>
              <div className="person-name"><T k="home.inhaber.name" /></div>
              <div className="person-role"><T k="home.inhaber.rolle" /></div>
            </div>
            <Link to="/ueber-uns" className="textlink"><T k="home.inhaber.cta" /> <Icon name="arrow-right" size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Stimmen */}
      <section className="shell section">
        <h2 className="h-section stimmen__title">
          <T k="home.stimmen.titel" />
        </h2>
        <Testimonials numbers={TESTIMONIAL_NUMBERS} />
      </section>

      {/* Team */}
      <section className="section--tinted">
        <div className="shell section">
          <div className="section-head">
            <h2 className="h-section">
              <T k="home.team.titel" />
            </h2>
            <p className="section-head__text">
              <T k="home.team.text" />
            </p>
          </div>
          <TeamGrid />
        </div>
      </section>

      {/* Aktuelles */}
      <section className="shell section">
        <div className="section-head section-head--row">
          <h2 className="h-section">
            <T k="home.aktuelles.titel" />
          </h2>
          <Link to="/aktuelles" className="textlink">
            <T k="home.aktuelles.link" /> <Icon name="arrow-right" size={16} />
          </Link>
        </div>
        <div className="autogrid">
          {news.slice(0, 3).map((item) => (
            <Link key={item.slug} to={newsLink(item)} className="news-tile">
              <div className="media">
                <img src={mediaUrl(item.image)} alt={item.imageAlt} loading="lazy" />
              </div>
              <div className="news-tile__date">{newsDate(item)}</div>
              <div className="news-tile__title">{item.title}</div>
            </Link>
          ))}
        </div>
      </section>

      <ContactSection />
    </>
  );
};

export default Home;
